/**
 * Tipi del modulo Carichi Esterni · frontend.
 * Replicato dal backend (apps/backend/src/models/session.ts + enums/sessions.ts).
 */

export const SESSION_SPORT = {
  FOOTBALL: 'football',
  RUGBY: 'rugby',
  BASKETBALL: 'basketball',
  VOLLEYBALL: 'volleyball',
  RUNNING: 'running',
  CYCLING: 'cycling',
  INDOOR_CYCLING: 'indoor_cycling',
  MTB: 'mtb',
  GRAVEL: 'gravel',
  TRAIL_RUNNING: 'trail_running',
  OTHER: 'other',
} as const;
export type SessionSport = (typeof SESSION_SPORT)[keyof typeof SESSION_SPORT];

export const SESSION_TYPE = {
  TRAINING: 'training',
  MATCH: 'match',
  FRIENDLY: 'friendly',
  TEST: 'test',
  INDIVIDUAL: 'individual',
  REHAB: 'rehab',
} as const;
export type SessionType = (typeof SESSION_TYPE)[keyof typeof SESSION_TYPE];

export const QUALITY_CLASS = {
  OK: 'ok',
  GOOD: 'good',
  PARTIAL: 'partial',
  INSUFFICIENT: 'insufficient',
  MANUAL_ENTRY: 'manual_entry',
} as const;
export type QualityClass = (typeof QUALITY_CLASS)[keyof typeof QUALITY_CLASS];

export const TABLE_MODE = {
  DAY: 'day',
  WEEK_MATRIX: 'weekMatrix',
  AGGREGATE: 'aggregate',
  PER_SESSION: 'perSession',
} as const;
export type TableMode = (typeof TABLE_MODE)[keyof typeof TABLE_MODE];

export const UNIT_MODE = {
  ABSOLUTE: 'absolute',
  PER_MIN: 'per_min',
} as const;
export type UnitMode = (typeof UNIT_MODE)[keyof typeof UNIT_MODE];

export const CELL_RENDERING = {
  VALUE: 'value',
  DUAL: 'dual',
  SPARK: 'spark',
} as const;
export type CellRendering = (typeof CELL_RENDERING)[keyof typeof CELL_RENDERING];

export const RESOURCE_SCOPE = {
  USER: 'user',
  TEAM: 'team',
} as const;
export type ResourceScope = (typeof RESOURCE_SCOPE)[keyof typeof RESOURCE_SCOPE];

export const SYSTEM_PRESET_ID = {
  KAMA_STANDARD: 'preset_kama_standard',
  ONLY_VOLUME: 'preset_only_volume',
  ONLY_INTENSITY: 'preset_only_intensity',
  METABOLIC_FOCUS: 'preset_metabolic_focus',
  ENDURANCE_RUNNING: 'preset_endurance_running_standard',
  ENDURANCE_CYCLING: 'preset_endurance_cycling_standard',
} as const;

export interface IMetricDef {
  id: string;
  label: string;
  group:
    | 'identification'
    | 'internal'
    | 'metabolic'
    | 'volume'
    | 'events'
    | 'advanced'
    | 'running'
    | 'cycling'
    | 'endurance_common';
  unit: string;
  aggregation: string;
  isVolumeMetric: boolean;
  accessor: string;
  decimals?: number;
}

export interface ISystemPreset {
  id: string;
  name: string;
  description: string;
  columns: string[];
  requiresProvider?: string[];
}

export interface ICatalogResponse {
  metrics: IMetricDef[];
  systemPresets: ISystemPreset[];
}

export type HeatmapStrategy =
  | { kind: 'percentile'; sampleNote?: string; breaks: number[] }
  | { kind: 'absolute'; sampleNote: string; breaks: number[] }
  | { kind: 'none'; sampleNote: string };

export interface IAthleteRow {
  athleteId: string;
  athleteName: string;
  position?: string;
  sessionsCount: number;
  totalDuration: number;
  values: Record<string, number | undefined>;
}

export interface IMetricSummary {
  sum?: number;
  mean?: number;
  stdDev?: number;
  cv?: number;
  max?: number;
  min?: number;
}

export interface ISummary {
  athletesIncluded: number;
  athletesTotal: number;
  metrics: Record<string, IMetricSummary>;
}

export interface ITableMeta {
  teamId: string;
  mode: TableMode;
  periodStart: string;
  periodEnd: string;
  unitMode: UnitMode;
  columns: string[];
  presetUsed?: string;
  sampleSize: number;
  heatmap: Record<string, HeatmapStrategy>;
}

export interface ITableResponse {
  meta: ITableMeta;
  rows: IAthleteRow[];
  summary: ISummary;
  dispersion: Record<string, 'omogen' | 'media' | 'alta' | 'na'>;
}

export interface IUserColumnPreset {
  _id: string;
  name: string;
  ownerId: string;
  scope: ResourceScope;
  teamId?: string;
  directorId: string;
  columns: string[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ICustomGroup {
  _id: string;
  name: string;
  description?: string;
  color?: string;
  athleteIds: string[];
  ownerId: string;
  scope: ResourceScope;
  teamId?: string;
  createdAt?: string;
}

export interface ISavedView {
  _id: string;
  name: string;
  description?: string;
  color?: string;
  ownerId: string;
  scope: ResourceScope;
  mode: TableMode;
  presetId?: string;
  kpiMetricId?: string;
  filters?: Record<string, unknown>;
  cellRendering?: CellRendering;
  unitMode?: UnitMode;
}

export interface ISession {
  _id: string;
  directorId: string;
  teamId?: string;
  athleteIds: string[];
  date: string;
  duration?: number;
  sport: SessionSport;
  sessionType: SessionType;
  name?: string;
  notes?: string;
  qualityClass: QualityClass;
  commonMetrics?: Record<string, number>;
  internalLoad?: Record<string, number | number[]>;
  enduranceMetrics?: Record<string, number>;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  // M6.2 cross-team + data-type fields
  crossTeamImport?: boolean;
  originalFileTeam?: string | null;
  importReason?: string | null;
  sessionScope?: 'team' | 'individual_athlete';
  dataType?: 'gps_full' | 'cardio_only' | 'running_cycling' | 'manual';
  isTeamContribution?: boolean;
  // M6.2 audit riassegnazioni
  reassignmentHistory?: Array<{
    fromTeamId: string;
    toTeamId: string;
    by: string;
    at: string;
    reason?: string;
  }>;
}

export interface ICreateSessionInput {
  teamId?: string;
  athleteIds: string[];
  date: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
  sport: SessionSport;
  sessionType: SessionType;
  name?: string;
  notes?: string;
  qualityClass?: QualityClass;
  commonMetrics?: Record<string, number>;
  internalLoad?: Record<string, number | number[]>;
  linkedEventId?: string;
  linkedAssignmentId?: string;
}
